import type Mithril from 'mithril';
import Component from 'flarum/common/Component';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Link from 'flarum/common/components/Link';
import Discussion from 'flarum/common/models/Discussion';
import { ApiPayloadPlural } from 'flarum/common/Store';
import { truncate } from 'flarum/common/utils/string';
import abbreviateNumber from 'flarum/common/utils/abbreviateNumber';
import app from 'flarum/forum/app';

export interface ExtraHeroAttrs {
  bannerUrl?: string;
  bannerLink?: string;
  tagSlug: string;
  sortType?: string;
}

/**
 * The `WelcomeHero` component displays a hero that welcomes the user to the
 * forum.
 */
export default class ExtraHero extends Component<ExtraHeroAttrs> {
  loading = false;

  data: Discussion[] = [];

  oninit(vnode: Mithril.Vnode<ExtraHeroAttrs, this>) {
    super.oninit(vnode);
    this.queryDiscussions();
  }

  view(vnode: Mithril.Vnode<ExtraHeroAttrs, this>) {
    return (
      <header className="Hero ExtraHero">
        <div className="container">
          {this.bannerView()}
          {this.discussionView()}
        </div>
      </header>
    );
  }

  bannerView() {
    const { bannerUrl, bannerLink } = this.attrs;

    return (
      <div className="ExtraHero-banner">
        <Link href={bannerLink} target="_blank" style={{ '--banner-url': `url(${bannerUrl})` }} />
      </div>
    );
  }

  discussionView() {
    const [first, ...others] = this.data;

    return (
      <div className="ExtraHero-discussion">
        {this.loading && <LoadingIndicator />}
        {!this.loading && !this.data.length && (
          <div className="Placeholder">
            <p>这里空空如也...</p>
          </div>
        )}
        {first && this.discussionFirst(first)}
        <div className="ExtraHero-discussion-items">{others.map((item) => this.discussionItem(item))}</div>
      </div>
    );
  }

  discussionFirst(discussion: Discussion) {
    const firstPost = discussion.firstPost();
    const excerpt = firstPost ? truncate(firstPost.contentPlain() || '', 80) : null;

    return (
      <Link className="ExtraHero-discussion-first" href={app.route.discussion(discussion)}>
        <div className="ExtraHero-discussion-item-title title">{discussion.title()}</div>
        <div className="ExtraHero-discussion-item-excerpt excerpt">{excerpt}</div>
      </Link>
    );
  }

  discussionItem(discussion: Discussion) {
    const user = discussion.user();
    const name = user ? user.displayName() : '';
    const canViewNumber = discussion.canViewNumber?.();
    let infoNode = <span>{name}</span>;

    if (canViewNumber) {
      const viewCount = discussion.viewCount();
      const number = app.forum.attribute('abbrNumber') == 1 ? abbreviateNumber(viewCount) : viewCount;
      infoNode = (
        <div className="item-discussion-views">
          <span>{number}</span>
        </div>
      );
    }
    const itemViews = null;

    return (
      <Link className="ExtraHero-discussion-item" href={app.route.discussion(discussion)}>
        <i className="icon" />
        <div className="ExtraHero-discussion-item-title title">{discussion.title()}</div>
        <div className="ExtraHero-discussion-item-info info">{infoNode}</div>
      </Link>
    );
  }

  queryDiscussions() {
    const { tagSlug, sortType } = this.attrs;

    this.loading = true;
    m.redraw();

    app
      .request<ApiPayloadPlural>({
        url: app.forum.attribute('apiUrl') + '/discussions',
        method: 'GET',
        params: {
          include: 'user,lastPostedUser,firstPost',
          'filter[tag]': tagSlug,
          sort: !sortType || sortType == ' ' ? undefined : sortType,
          'page[offset]': 0,
          'page[limit]': 11,
        },
      })
      .then((payload) => (this.data = app.store.pushPayload(payload) as any))
      .catch(console.error)
      .finally(() => {
        this.loading = false;
        m.redraw();
      });
  }
}
